import React from "react";
import { useForm } from "react-hook-form";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import ReactStars from "react-rating-stars-component";
import { axiosInstanceWithToken } from "@/helpers/axios";
import { errorPopup, successPopup } from "@/helpers/swirlfire";

// Validation schema
const validationSchema = yup.object({
    review_message: yup
        .string()
        .required("Written review is required")
        .max(500, "Review cannot exceed 500 characters"),
    private_message: yup.string().max(500, "Feedback cannot exceed 500 characters"),
    ratings: yup.object().shape({
        Overall_Satisfaction: yup
            .number()
            .min(1, "Overall Satisfaction rating is required")
            .required("Overall Satisfaction rating is required"),
        Cleanliness: yup.number().min(1, "Cleanliness rating is required").required(),
        Accuracy: yup.number().min(1, "Accuracy rating is required").required(),
        Communication: yup.number().min(1, "Communication rating is required").required(),
        Location: yup.number().min(1, "Location rating is required").required(),
        Check_in: yup.number().min(1, "Check-in rating is required").required(),
        Value: yup.number().min(1, "Value rating is required").required(),
    }),
});

export const BookingTailwindModal = ({ isOpen, handleClose, selectedId, type }) => {
    const {
        register,
        setValue,
        formState: { errors },
        watch
    } = useForm({
        resolver: yupResolver(validationSchema),
        defaultValues: {
            review_message: "",
            private_message: "",
            ratings: {
                Overall_Satisfaction: 0,
                Cleanliness: 0,
                Accuracy: 0,
                Communication: 0,
                Location: 0,
                Check_in: 0,
                Value: 0,
            },
        },
    });

    const user_id = localStorage.getItem('data')?.id;

    function calculateAverage(ratings) {
        const values = Object.values(ratings); // Extract all rating values
        const total = values.reduce((sum, rating) => sum + rating, 0); // Sum up all the ratings
        const average = total / values.length; // Calculate the average
        return average.toFixed(2); // Return the average rounded to 2 decimal places
    }

    const onSubmit = async () => {
        const data = {
            booking_id: selectedId?.booking_id,
            listing_id: selectedId?.listing_id,
            user_id,
            type,
            rating: calculateAverage(watch("ratings")),
            review: watch("review_message"),
            meta_data: JSON.stringify({
                listing_id: selectedId?.listing_id,
                ...watch()
            }),
        }

        if (type === 'GUEST_TO_BOOKING') {
            data.private_message = watch("private_message")
        }

        const formData = new FormData();

        Object.entries(data).forEach(([key, value]) => {
            formData.append(key, value);
        });


        await axiosInstanceWithToken.post(`/review`, formData).then(response => {
            // console.log(response);
            if (response?.data?.status === "success") {
                successPopup('', response.data.message);
                handleClose(); // Close modal after submission
            } else {
                errorPopup('', response.data.message);
            }
        }).catch(error => {
            console.error(error);
            // Handle error in case of submission failure
        });
    };

    if (!isOpen) return null;

    const ratingCategories = [
        { label: "Overall_Satisfaction", description: "How much you enjoyed the experience of staying at this property" },
        { label: "Cleanliness", description: "How clean and tidy the property was upon arrival" },
        { label: "Accuracy", description: "How well the listing description and photos matched the property" },
        { label: "Communication", description: "How responsive and clear your Host's communication was" },
        { label: "Location", description: "How convenient and appealing the property's location was" },
        { label: "Check_in", description: "How easy and smooth the check-in process was" },
        { label: "Value", description: "How you perceived the value of your stay relative to the price you paid" },
    ];

    return (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black bg-opacity-50">
            <div className="w-full max-w-lg bg-white rounded-lg shadow-lg">
                <div className="flex justify-between p-4 border-b">
                    <h2 className="text-xl font-semibold">Review</h2>
                    <button
                        className="flex items-center justify-center w-8 h-8 p-2 mb-2 bg-white rounded-full shadow-md hover:bg-gray-100"
                        onClick={handleClose}
                    >
                        <i className="text-gray-600 fas fa-times"></i>
                    </button>
                </div>

                <div className="p-4 overflow-y-scroll" style={{ height: "400px" }}>
                    <form onSubmit={(e) => {
                        e.preventDefault();
                        onSubmit();
                    }} className="space-y-4">
                        <div className="space-y-4">
                            {ratingCategories.map((item, index) => (
                                <fieldset
                                    key={index}
                                    className="flex flex-col p-4 text-center border-soft"
                                >
                                    <label className="block text-sm font-medium">{item.label}</label>
                                    <span className="block mb-2 text-xs">{item.description}</span>
                                    <ReactStars
                                        count={5}
                                        onChange={(newRating) => setValue(`ratings.${item.label}`, newRating)}
                                        size={30}
                                        activeColor="#ffd700"
                                    />
                                    {errors.ratings?.[item.label] && (
                                        <p className="text-sm text-red-500">{errors.ratings[item.label]?.message}</p>
                                    )}
                                </fieldset>
                            ))}
                        </div>

                        <fieldset>
                            <textarea
                                {...register("review_message")}
                                className="w-full p-2 border rounded-md"
                                rows={3}
                                placeholder="Written Review: Please provide a written account of your experience, highlighting positive aspects and any areas for improvement."
                            />
                            {errors.review_message && <p className="text-sm text-red-500">{errors.review_message.message}</p>}
                        </fieldset>

                        {type === 'GUEST_TO_BOOKING' &&
                            <fieldset>
                                <textarea
                                    {...register("private_message")}
                                    className="w-full p-2 border rounded-md"
                                    rows={3}
                                    placeholder="Optional - Private Feedback. The public will not see this."
                                />
                                {errors.private_message && <p className="text-sm text-red-500">{errors.private_message.message}</p>}
                            </fieldset>

                        }
                        <div className="flex justify-end pb-5">
                            <button type="submit" className="bg-[#8ec639] text-white px-4 py-2 rounded-md">
                                Submit
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
};
